// src/components/manuscript/StructureFormatToolbar.jsx
//
// The formatting toolbar on a manuscript structure template.
//
// A sibling of `EditorFormatBar`, not a reuse of it. That bar is bound to the
// editor's contenteditable — it formats the current selection, prevents blur on
// mousedown, and is styled through the `editor-format-bar` CSS class — and it has
// no strikethrough or alignment. This one sets formatting for a whole template
// element: there is no selection, the value is stored on the element, and it
// applies to that element's text in the preview.
//
// **The named style is a preset, not a fourth knob.** Picking one writes size,
// font, alignment and weight at once, and the other controls adjust from there —
// the same contract `EditorToolbar` has always had, from the same `TEXT_STYLES`
// list. That is why it can sit beside the font and size selects without the three
// of them fighting: one writes the other two.
//
// The author chooses their own typography here. This page is where they design
// the book, so the font list is the app's whole list rather than a house style.
import React from 'react';
import { Box, Tooltip } from '@mui/material';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import StrikethroughSIcon from '@mui/icons-material/StrikethroughS';
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft';
import FormatAlignCenterIcon from '@mui/icons-material/FormatAlignCenter';
import FormatAlignRightIcon from '@mui/icons-material/FormatAlignRight';
import FormatAlignJustifyIcon from '@mui/icons-material/FormatAlignJustify';

import { FONT_FAMILIES, FONT_SIZES, TEXT_STYLES } from '../../config/fonts';
import { SP, FONT } from './structureTokens';

const MARKS = [
  { key: 'bold', label: 'Bold', Icon: FormatBoldIcon },
  { key: 'italic', label: 'Italic', Icon: FormatItalicIcon },
  { key: 'underline', label: 'Underline', Icon: FormatUnderlinedIcon },
  { key: 'strike', label: 'Strikethrough', Icon: StrikethroughSIcon },
];

const ALIGNMENTS = [
  { key: 'left', label: 'Align left', Icon: FormatAlignLeftIcon },
  { key: 'center', label: 'Align center', Icon: FormatAlignCenterIcon },
  { key: 'right', label: 'Align right', Icon: FormatAlignRightIcon },
  { key: 'justify', label: 'Justify', Icon: FormatAlignJustifyIcon },
];

/** `'24px'` → `24`. The selects speak CSS; the stored value is a number. */
const toPx = (size) => parseInt(size, 10);

function ToolbarButton({ label, active, onClick, Icon }) {
  return (
    <Tooltip title={label}>
      <Box
        component="button"
        type="button"
        aria-label={label}
        aria-pressed={active}
        onClick={onClick}
        sx={{
          width: 30,
          height: 30,
          p: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: 'none',
          borderRadius: '3px',
          cursor: 'pointer',
          transition: 'all .15s ease',
          backgroundColor: active ? SP.purpleDeep : SP.gold,
          color: active ? SP.goldLight : SP.purpleDeep,
          boxShadow: active ? `inset 0 0 0 1.5px ${SP.gold}` : 'none',
          '&:hover': { backgroundColor: active ? SP.purpleDeep : SP.goldLight },
          '&:focus-visible': { outline: `2px solid ${SP.goldLight}`, outlineOffset: 2 },
        }}
      >
        <Icon sx={{ fontSize: 17 }} />
      </Box>
    </Tooltip>
  );
}

function ToolbarSelect({ label, value, onChange, children, sx }) {
  return (
    <Box
      component="select"
      aria-label={label}
      title={label}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      sx={{
        fontFamily: FONT.sans,
        fontSize: 11.5,
        fontWeight: 500,
        px: 1.25,
        py: 0.9,
        maxWidth: 150,
        border: 'none',
        borderRadius: '3px',
        backgroundColor: SP.gold,
        color: SP.purpleDeep,
        cursor: 'pointer',
        '&:hover': { backgroundColor: SP.goldLight },
        '&:focus-visible': { outline: `2px solid ${SP.goldLight}`, outlineOffset: 2 },
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}

const Divider = () => (
  <Box sx={{ width: '1px', alignSelf: 'stretch', mx: 0.25, backgroundColor: 'rgba(255,255,255,0.15)' }} />
);

export default function StructureFormatToolbar({ format, onChange }) {
  /**
   * Apply a named style. It writes the controls it owns and leaves the rest —
   * underline, strikethrough and the element's position are the author's, and a
   * preset has no business clearing them.
   */
  const applyTextStyle = (value) => {
    const style = TEXT_STYLES.find((s) => s.value === value);
    if (!style) return;
    onChange({
      ...format,
      textStyle: style.value,
      fontSize: toPx(style.size),
      fontFamily: style.font,
      align: style.align,
      bold: style.bold,
      italic: style.italic,
    });
  };

  // Editing a control the preset owns means the text is no longer that preset.
  // Clearing the label is honest; leaving it saying "Title" over 11px body text
  // would not be.
  const patch = (fields) => onChange({ ...format, textStyle: '', ...fields });

  return (
    <Box
      role="toolbar"
      aria-label="Template text formatting"
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 1,
        mt: 2.25,
        px: 1.75,
        py: 1.5,
        borderRadius: '6px',
        backgroundColor: SP.purpleDeep,
      }}
    >
      <ToolbarSelect label="Text style" value={format.textStyle || ''} onChange={applyTextStyle}>
        {!format.textStyle && <option value="">Custom</option>}
        {TEXT_STYLES.map((s) => (
          <option key={s.value} value={s.value}>{s.label}</option>
        ))}
      </ToolbarSelect>

      <ToolbarSelect
        label="Font"
        value={format.fontFamily || ''}
        onChange={(fontFamily) => patch({ fontFamily })}
      >
        {/* A font retired from the list, or one from a newer build, still has to
            be visible as the current choice rather than showing as blank. */}
        {format.fontFamily && !FONT_FAMILIES.some((f) => f.name === format.fontFamily) && (
          <option value={format.fontFamily}>{format.fontFamily}</option>
        )}
        {FONT_FAMILIES.map((f) => (
          <option key={f.name} value={f.name} style={{ fontFamily: f.stack }}>{f.name}</option>
        ))}
      </ToolbarSelect>

      <ToolbarSelect
        label="Font size"
        value={`${format.fontSize || 16}px`}
        onChange={(size) => patch({ fontSize: toPx(size) })}
        sx={{ maxWidth: 80 }}
      >
        {FONT_SIZES.map((size) => (
          <option key={size} value={size}>{size}</option>
        ))}
      </ToolbarSelect>

      <Divider />

      <Box sx={{ display: 'flex', gap: 0.5 }}>
        {MARKS.map(({ key, label, Icon }) => (
          <ToolbarButton
            key={key}
            label={label}
            Icon={Icon}
            active={Boolean(format[key])}
            onClick={() => {
              // Bold and italic are part of a named style; underline and
              // strikethrough are not, so only the first two clear the label.
              const owned = key === 'bold' || key === 'italic';
              const next = { ...format, [key]: !format[key] };
              onChange(owned ? { ...next, textStyle: '' } : next);
            }}
          />
        ))}
      </Box>

      <Divider />

      <Box sx={{ display: 'flex', gap: 0.5 }}>
        {ALIGNMENTS.map(({ key, label, Icon }) => (
          <ToolbarButton
            key={key}
            label={label}
            Icon={Icon}
            active={format.align === key}
            onClick={() => patch({ align: key })}
          />
        ))}
      </Box>
    </Box>
  );
}
